import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useChartColors } from "./chartColors";
import { Panel, LoadingRows, ErrorState, EmptyState } from "./Panel";
import type { AnalyticsEnvelope, TasksLoad, AnalyticsViewProps } from "./types";

type TasksLoadData = AnalyticsEnvelope & TasksLoad;

export function TasksLoadView({
  data,
  loading,
  error,
}: AnalyticsViewProps<TasksLoadData>) {
  const { t } = useTranslation();
  const colors = useChartColors();
  const rows = data?.series ?? [];
  return (
    <Panel title={t("homework.analytics.tasks.title")}>
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorState message={t("homework.analytics.error")} />
      ) : !rows.length ? (
        <EmptyState message={t("homework.analytics.empty")} />
      ) : (
        <>
          <div role="img" aria-label={t("homework.analytics.tasks.title")}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="period" stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} />
                <YAxis stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} width={36} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="opened"
                  name={t("homework.analytics.tasks.opened")}
                  fill={colors.series(1)}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="closed"
                  name={t("homework.analytics.tasks.closed")}
                  fill={colors.series(2)}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 space-y-0.5 text-[12px]">
            {(data?.load_per_user ?? []).slice(0, 6).map((u) => (
              <li key={u.user_id} className="flex justify-between">
                <span>{u.name}</span>
                <span className="tabular-nums">
                  {t("homework.analytics.tasks.openCount", { count: u.open_count })}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {data?.caveat && <p className="mt-2 text-[11px] text-muted-foreground">{data.caveat}</p>}
    </Panel>
  );
}
